import {
  useCanvasFileRecords,
  useHydrateFileRecordEntity,
} from "@/api/fileRecords";
import { ROUTES } from "@/constants/routes";
import { useAppNavigate } from "@/hooks/pane-routing";
import {
  buildCanvasNodeKind,
  getActionIcon,
  getActionLabel,
  getObjectActionDefinition,
  resolvedActionsToContextMenuActions,
  useCanvasEntityObjectActions,
} from "@/object-actions";
import type { ObjectActionId } from "@/object-actions/types";
import { useCanvasEntityNavigationHandlers } from "@/pages/canvas/hooks/useCanvasEntityNavigationHandlers";
import { useCanvasFileRecordMenuHandlerDeps } from "@/pages/canvas/hooks/useCanvasFileRecordMenuHandlerDeps";
import type { EntityNodeProps } from "@/pages/canvas/types/canvasNodeData";
import { openCanvasEntityTabUrl } from "@/pages/canvas/utils/resolveCanvasEntityOpenTabUrl";
import { resolveFileRecordFromList } from "@/pages/canvas/utils/resolveFileRecordFromList";
import { useOpenFileRecord } from "@/panes/file-record-detail";
import { cn } from "@/utils";
import { route } from "@/utils/route";
import type { FileRecord } from "@cohesive/api-client";
import {
  BaseEntityCardFloatingToolbar,
  BaseEntityCardQuickChat,
  CanvasEntityCard,
  FileRecordCardSkeleton,
  getEntityData,
  type ContextMenuAction,
  type Entity,
  type FileRecordSkeletonKind,
} from "@cohesive/ui-components";
import { NodeResizer, useUpdateNodeInternals } from "@xyflow/react";
import { AnimatePresence } from "framer-motion";
import { AlertCircle, ExternalLink, Play } from "lucide-react";
import {
  createElement,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { LiveProcess } from "../components/LiveProcess";
import { useCanvasApi } from "../context/CanvasContext";
import { useCanvasFloatingChat } from "../context/CanvasFloatingChatContext";
import {
  useCanvasLocalFilePreview,
  useCanvasLocalFilePreviewActions,
} from "../context/CanvasLocalFilePreviewContext";
import { useCanvasProcess } from "../context/CanvasProcessContext";
import {
  useCanvasSelectionCount,
  useIsMultiSelectToolbarOpen,
} from "../context/CanvasSelectionCountContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useOpenNodeContextMenu } from "../hooks/useOpenNodeContextMenu";
import { useSectionPreviewHighlight } from "../hooks/useSectionPreviewHighlight";
import { useVideoHandoffReady } from "../hooks/useVideoHandoffReady";
import {
  fileRecordIsProcessed,
  mergeFileRecordWithLocalPreview,
  shouldRevokeLocalFilePreview,
  shouldShowFileRecordSkeleton,
} from "../utils/fileRecordDisplay";
import { resolveGenericEntityPaneTarget } from "../utils/genericEntityPane";
import {
  DEFAULT_FILE_NODE_HEIGHT,
  getEntityMediaDisplayBox,
  getFileRecordMediaDisplayBoxFromMetadata,
} from "../utils/imageDimensions";
import { CanvasConnectHandles } from "./shared/CanvasConnectHandles";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";

// Temporary: file entity node toolbars only surface these utilities (in this
// order). Chat + delete render separately; the rest live under "More".
const FILE_ENTITY_TOOLBAR_UTILITY_ORDER: ObjectActionId[] = [
  "share",
  "download",
  "open",
];

// Default card width — used when no width is stored in Loro yet.
const DEFAULT_WIDTH = 350;

// Matches fitImageNodeSize(..., minShortEdge); generic file nodes stay at 100.
const CANVAS_FILE_RESIZER_MIN = 8;
const DEFAULT_RESIZER_MIN = 100;

// Several classes of legacy entities are intentionally not rendered on the
// canvas surface anymore. All wrappers return `null` from the React Flow node
// component, which fully hides the node — selection rings, handles, the lot —
// without touching Loro state. Backend cleanup (deleting the rows, dropping
// the supporting tables) happens in a later pass.
//
//   1. `type: "artifact"` — content migrated into file_records.
//
//   2. `base: "file"` AND (platform === "cohesive" OR type === "canvas_file")
//      — the legacy rag_files-backed canvas uploads. External `base: "file"`
//      entities (Google Drive, Dropbox, Slack file shares, etc.) keep
//      rendering through the normal external-file path.
//
//   3. `base: "image"` | `base: "video"` — legacy media nodes.
//      These still render in chat via the legacyFileEntityDataToFileRecord
//      adapter; they are hidden on canvas only.
//
// The guard runs *before* any hooks in the inner component, so we don't pay
// for stale fetches / effects on hidden nodes.
function isLegacyHiddenEntityProps(props: EntityNodeProps): boolean {
  const e = props.data?.entity as
    | { base?: string; type?: string; platform?: string }
    | undefined;
  if (!e) return false;
  if (e.type === "artifact") return true;
  if (e.base === "image" || e.base === "video") return true;
  if (
    e.base === "file" &&
    (e.platform === "cohesive" || e.type === "canvas_file")
  ) {
    return true;
  }
  return false;
}

export const EntityNode = memo(function EntityNode(props: EntityNodeProps) {
  if (isLegacyHiddenEntityProps(props)) return null;
  return <EntityNodeImpl {...props} />;
});

function EntityNodeImpl({
  id,
  data,
  selected,
  dragging,
  width,
  height,
  positionAbsoluteX,
  positionAbsoluteY,
}: EntityNodeProps) {
  const canvas = useCanvasApi();
  const { canEdit } = canvas;
  const {
    dispatchProcess,
    getProcessesForNode,
    isProcessing,
    highlightedNodeIds,
  } = useCanvasProcess();
  const { isResizingRef } = useCanvasUi();
  const isHighlighted = highlightedNodeIds.has(id);
  const [isToolbarHovered, setIsToolbarHovered] = useState(false);
  const [showChatInput, setShowChatInput] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const appNavigate = useAppNavigate();
  const openFileRecord = useOpenFileRecord();
  const { openSession: openCanvasFloatingSession } = useCanvasFloatingChat();
  const { canvasId } = useParams<{ canvasId: string }>();

  // O(1) — subscribes to only the selection count scalar, not full node array.
  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();
  const isSectionPreviewMember = useSectionPreviewHighlight(id);

  // Clear chat latch on multi-select or deselect — node stays selected during
  // multi-select, so !selected alone would leave showChatInput true and remount
  // chat UI when returning to single-select.
  useEffect(() => {
    if (isMultiSelectToolbarOpen || !selected) {
      setShowChatInput(false);
    }
  }, [isMultiSelectToolbarOpen, selected]);

  const isFileRecordEntity = !!(
    data?.entity &&
    "base" in data.entity &&
    data.entity.base === "file_record"
  );

  const localPreview = useCanvasLocalFilePreview(id);
  const { revoke: revokeLocalPreview } = useCanvasLocalFilePreviewActions();

  const { entity: hydratedFileRecordEntity } = useHydrateFileRecordEntity(
    isFileRecordEntity ? (data?.entity as any) : null,
  );

  // Hydrated file records carry full backend metadata (content_type,
  // preview_url, etc.) that the toolbar / preview render against. Other
  // entity types pass through the raw Loro entity unchanged.
  const effectiveEntity = useMemo(() => {
    if (isFileRecordEntity && hydratedFileRecordEntity) {
      return hydratedFileRecordEntity;
    }
    return data?.entity;
  }, [isFileRecordEntity, data?.entity, hydratedFileRecordEntity]);

  const layoutEntity = effectiveEntity ?? data?.entity;

  const isVideoLocalPreview =
    !!localPreview && localPreview.contentType.startsWith("video/");

  const serverFileRecordData = isFileRecordEntity
    ? (effectiveEntity?.data as
        | {
            status?: string;
            url?: string;
            preview_urls?: Record<string, string> | null;
          }
        | undefined)
    : undefined;

  const serverVideoUrl =
    isVideoLocalPreview &&
    fileRecordIsProcessed(serverFileRecordData?.status) &&
    serverFileRecordData?.url
      ? serverFileRecordData.url
      : undefined;

  const videoHandoffReady = useVideoHandoffReady(
    serverVideoUrl,
    !!serverVideoUrl,
  );

  const mergeOptions = useMemo(
    () => ({ videoHandoffReady }),
    [videoHandoffReady],
  );

  const fileRecordCardEntity = useMemo(() => {
    const base = layoutEntity;
    if (!isFileRecordEntity || !base || !("data" in base) || !localPreview) {
      return base;
    }
    const mergedData = mergeFileRecordWithLocalPreview(
      base.data as {
        status?: string;
        url?: string;
        content_type?: string;
        preview_urls?: Record<string, string> | null;
      },
      localPreview,
      mergeOptions,
    );
    if (mergedData === base.data) return base;
    return { ...base, data: mergedData } as Entity;
  }, [isFileRecordEntity, layoutEntity, localPreview, mergeOptions]);

  // Revoke blob for images once processed; videos revoke in onVideoDisplayReady
  // after the server stream paints (see useVideoHandoffReady + thumbnail hold).
  useEffect(() => {
    if (!localPreview) return;
    if (shouldRevokeLocalFilePreview(serverFileRecordData, localPreview)) {
      revokeLocalPreview(id);
    }
  }, [id, localPreview, serverFileRecordData, revokeLocalPreview]);

  const handleVideoDisplayReady = useCallback(() => {
    if (!localPreview?.contentType.startsWith("video/")) return;
    if (!fileRecordIsProcessed(serverFileRecordData?.status)) return;
    if (!videoHandoffReady) return;
    revokeLocalPreview(id);
  }, [
    id,
    localPreview,
    serverFileRecordData?.status,
    videoHandoffReady,
    revokeLocalPreview,
  ]);

  // Determine resize behaviour from entity type
  const entity = layoutEntity;
  // HTML file records get free-form resize (no aspect lock).
  const isHtmlFileRecord =
    !!entity &&
    "base" in entity &&
    entity.base === "file_record" &&
    ((entity.data as { content_type?: string })?.content_type ?? "")
      .split(";")[0]
      .trim() === "text/html";
  // Generic entities with a cover image render as an embedded thumbnail tile
  // (see CanvasEntityCard); treat them like file tiles for sizing + resize.
  const isGenericTile = !!(
    entity &&
    "base" in entity &&
    entity.base === "generic" &&
    (entity.data as { image_url?: string })?.image_url
  );
  const isSquareResizable = !!(
    entity &&
    "base" in entity &&
    (entity.base === "file" ||
      (entity.base === "file_record" && !isHtmlFileRecord) ||
      isGenericTile)
  );
  const fittedMediaBox = entity
    ? (getEntityMediaDisplayBox(entity) ??
      getFileRecordMediaDisplayBoxFromMetadata(entity))
    : null;
  // Preserve square locking for all square cards (image/video/file).
  // Fitted media boxes (e.g. image/* attachments) also keep their computed
  // aspect. HTML file records are excluded — they resize freely.
  const keepResizeAspect = isSquareResizable || fittedMediaBox != null;
  const resizerMin =
    fittedMediaBox != null ? CANVAS_FILE_RESIZER_MIN : DEFAULT_RESIZER_MIN;
  const isResizable = isSquareResizable || isHtmlFileRecord;

  // React Flow passes width=0 for nodes that haven't been measured yet.
  // Use || (not ??) so that 0 also falls back to the default.
  const nodeWidth = width || fittedMediaBox?.width || DEFAULT_WIDTH;
  // Explicit height: fitted media and file-ish nodes need a stable host box.
  // Agent-placed file_records can arrive with only id/title and no Loro
  // dimensions, so non-media records fall back to the default file tile
  // height instead of letting the embedded thumb collapse.
  const isFileRecord =
    !!entity && "base" in entity && entity.base === "file_record";
  const isLegacyFileBase =
    !!entity && "base" in entity && entity.base === "file";
  const nodeHeight =
    height && height > 0
      ? height
      : fittedMediaBox != null
        ? fittedMediaBox.height
        : isHtmlFileRecord
          ? undefined
          : isFileRecord || isLegacyFileBase || isGenericTile
            ? DEFAULT_FILE_NODE_HEIGHT
            : undefined;

  // Get processes for this node
  const processes = getProcessesForNode(id);

  // Navigate to flow session when clicking a process
  const handleProcessNavigate = useCallback(
    (flowSessionId: string) => {
      openCanvasFloatingSession(flowSessionId);
    },
    [openCanvasFloatingSession],
  );

  // Process actions — AI/automation only (rendered purple in toolbar)
  const entityActions = useMemo(() => {
    if (!layoutEntity) return [];

    const entity = layoutEntity;
    const actions: ContextMenuAction[] = [];

    // Action item — execute
    if ("base" in entity && entity.base === "action_item") {
      const entityData = getEntityData(entity);
      const canExecute = entityData?.status === "ready";
      if (canExecute) {
        actions.push({
          key: "execute-action-item",
          label: "Execute action item",
          icon: <Play className="h-5 w-5" />,
          disabled: isProcessing(id),
          onClick: () => {
            dispatchProcess({
              nodeId: id,
              entity,
              action: "execute_action_item",
            });
          },
        });
      }
    }

    return actions;
  }, [layoutEntity, id, dispatchProcess, isProcessing]);

  const handleDelete = () => {
    revokeLocalPreview(id);
    canvas.removeNode(id);
  };

  const handleChatSubmit = async (message: string) => {
    if (!data?.entity) return;

    const flowSessionId = await dispatchProcess({
      nodeId: id,
      entity: layoutEntity ?? data.entity,
      action: "chat",
      customPrompt: message,
    });

    setShowChatInput(false);

    if (flowSessionId && canvasId) {
      openCanvasFloatingSession(flowSessionId);
    }
  };

  // Suppress when MultiSelectFloatingToolbar is mounted (multiSelectNodes >= 2).
  // Single-select: hover, chat input, or selection. Hidden during drag and resize.
  const showToolbar =
    !dragging &&
    !isResizing &&
    !isMultiSelectToolbarOpen &&
    (isToolbarHovered ||
      showChatInput ||
      (selected && selectedNodeCount === 1));

  const handleEntityClick = async () => {
    if (!layoutEntity) return;

    const entity = layoutEntity;
    const entityData = getEntityData(entity);
    if (!entityData?.id) return;

    const targetPane = "rightPane";

    if ("type" in entity && (entity as any).type === "skill") {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.SKILLS.SHOW, {
          id: entityData.id.toString(),
        }),
      });
      return;
    }

    if ("base" in entity && entity.base === "file_record") {
      // FileRecordDetailPane reads the id directly; no extra route state
      // is needed. useOpenFileRecord handles the two-hop navigation
      // pattern for standalone sessions (irrelevant on canvas but safe).
      openFileRecord(String(entityData.id));
      return;
    }

    const isNotionDocument =
      ("type" in entity && (entity as any).type === "notion_mcp_document") ||
      ("base" in entity &&
        entity.base === "document" &&
        entity.platform === "notion");

    if (isNotionDocument) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.CANVAS_EXPERIMENTS.NOTION_DETAIL, {
          pageId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      (entity.base === "email" || entity.base === "email_thread") &&
      entity.platform === "gmail"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.CANVAS_EXPERIMENTS.EMAIL_DETAIL, {
          emailId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "github" &&
      (entity.base === "issue" ||
        ("type" in entity && entity.type === "github_project_item"))
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GITHUB.ISSUE_DETAIL, {
          issueId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "github" &&
      entity.base === "pull_request"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GITHUB.PR_DETAIL, {
          prId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "github" &&
      entity.base === "repo"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GITHUB.REPO_DETAIL, {
          repoId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "github" &&
      entity.base === "project"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GITHUB.PROJECT_DETAIL, {
          projectId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    if (
      "base" in entity &&
      entity.platform === "google-drive" &&
      entity.base === "file"
    ) {
      appNavigate({
        pane: targetPane,
        to: route(ROUTES.GOOGLE_DRIVE.FILE_DETAIL, {
          fileId: entityData.id.toString(),
        }),
        options: { state: { entity } },
      });
      return;
    }

    // Generic entities carry their real platform in data.platform_name — route
    // to that platform's dedicated pane. Unmapped platforms fall through to the
    // generic entity detail pane below.
    if ("base" in entity && entity.base === "generic") {
      const target = resolveGenericEntityPaneTarget(entity);
      if (target) {
        appNavigate({
          pane: targetPane,
          to: target.to,
          options: { state: target.state },
        });
        return;
      }
    }

    appNavigate({
      pane: targetPane,
      to: route(ROUTES.ENTITIES.DETAIL, {
        platform: entity.platform || "cohesive",
        entityId: entityData.id.toString(),
      }),
      options: { state: { entity } },
    });
  };

  const {
    handlers: entityNodeHandlers,
    hasWebLink,
    hasDetailView,
  } = useCanvasEntityNavigationHandlers(
    layoutEntity,
    canEdit,
    handleDelete,
    () => void handleEntityClick(),
  );

  const entityNodeKind = useMemo(
    () =>
      layoutEntity && "base" in layoutEntity
        ? buildCanvasNodeKind("entity", layoutEntity.base)
        : null,
    [layoutEntity],
  );

  // Canvas entities carry an id-only stub; prefer the cached API record so
  // toolbar file actions (share/publish/download) have `path` etc., matching
  // how the node context menu resolves it.
  const { data: canvasFileRecords = [] } = useCanvasFileRecords(canvasId);

  const fileRecordForExport = useMemo<FileRecord | null>(() => {
    if (
      !layoutEntity ||
      !("base" in layoutEntity) ||
      layoutEntity.base !== "file_record"
    ) {
      return null;
    }
    const stub = getEntityData(layoutEntity) as FileRecord;
    return resolveFileRecordFromList(stub, canvasFileRecords) ?? stub;
  }, [layoutEntity, canvasFileRecords]);

  // Shared canvas file-record handler deps (same set the node context menu
  // uses) so the toolbar's More menu gets the 1:1 lifecycle actions: Delete =
  // remove node + trash record; Hide on canvas = non-destructive node.hidden.
  const canvasFileRecordHandlerDeps = useCanvasFileRecordMenuHandlerDeps(
    canvasId,
    fileRecordForExport,
  );

  const { actions: entityUtilityRegistryActions } =
    useCanvasEntityObjectActions({
      kind: entityNodeKind,
      surface: "floating-toolbar",
      enabled: showToolbar && !!entityNodeKind,
      nodeHandlers: entityNodeHandlers,
      fileRecord: fileRecordForExport,
      fileRecordHandlerDeps: canvasFileRecordHandlerDeps ?? {
        onOpen: (record) => openFileRecord(record.id),
      },
      state: {
        canEdit,
        hasWebLink,
        hasDetailView,
      },
    });

  const utilityActions = useMemo(() => {
    // Temporary simplification: file entity node toolbars show only these
    // utilities (chat + delete render separately; everything else stays
    // available via the three-dot "More" menu). Order matches this list.
    const registryActions = isFileRecordEntity
      ? FILE_ENTITY_TOOLBAR_UTILITY_ORDER.flatMap((id) =>
          entityUtilityRegistryActions.filter((a) => a.id === id),
        )
      : entityUtilityRegistryActions;

    return resolvedActionsToContextMenuActions(
      registryActions,
      ["toolbar-utility"],
      {
        kind: entityNodeKind ?? { type: "canvas-node", nodeType: "entity" },
      },
    );
  }, [entityNodeKind, entityUtilityRegistryActions, isFileRecordEntity]);

  const openNodeContextMenu = useOpenNodeContextMenu();
  const handleMoreClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      openNodeContextMenu(id, e.currentTarget);
    },
    [id, openNodeContextMenu],
  );

  if (!data) {
    return (
      <div className="bg-card border-2 rounded-lg p-4 w-[350px] flex items-center justify-center h-24">
        <div className="text-sm text-muted-foreground">Invalid Entity Node</div>
      </div>
    );
  }

  // ── Skeleton ────────────────────────────────────────────────────────────────
  // Agent placement and collaborators see the shimmer until server media
  // lands. The dropping user's image/video uploads use client-only blob URLs
  // (CanvasLocalFilePreviewContext) so the real card renders immediately.
  const fileRecordData = isFileRecordEntity
    ? (layoutEntity?.data as
        | {
            status?: string;
            url?: string;
            content_type?: string;
            preview_urls?: Record<string, string> | null;
          }
        | undefined)
    : null;

  const showSkeleton = shouldShowFileRecordSkeleton({
    loading: data.loading,
    isFileRecordEntity,
    fileRecordData: isFileRecordEntity
      ? (effectiveEntity?.data as typeof fileRecordData)
      : null,
    localPreview,
    videoHandoffReady,
  });

  // Connect handles render only on the real card (below) — there's nothing
  // useful to wire an edge to on a loading skeleton or an errored node. Since
  // those handles therefore *appear* only when the node swaps from skeleton /
  // error into the card, React Flow needs to re-measure the node so edges (and
  // the live connection-line preview) attach to the freshly-mounted handles;
  // its cached handle bounds from the prior branch are stale/empty otherwise.
  // A node that mounts directly as the card already has its handles measured
  // normally, so we nudge RF only on the transition *into* the card, never on
  // initial mount. This also keeps the screenshot/capture path (where nodes
  // mount straight as cards) free of extra re-measure churn.
  const updateNodeInternals = useUpdateNodeInternals();
  const isCard = !data.error && !showSkeleton;
  const wasCardRef = useRef(isCard);
  useEffect(() => {
    if (isCard && !wasCardRef.current) {
      updateNodeInternals(id);
    }
    wasCardRef.current = isCard;
  }, [id, isCard, updateNodeInternals]);

  // ── Error state ────────────────────────────────────────────────────────────
  // URL ingestion failed: keep the node so the user can see the error message
  // and open / copy the original URL before deciding whether to delete.
  if (data.error) {
    const errW = width || DEFAULT_WIDTH;
    const errH = height || DEFAULT_FILE_NODE_HEIGHT;
    return (
      <div className="relative" style={{ width: errW, height: errH }}>
        {/* No connect handles on the error card — an errored node isn't a
            meaningful edge endpoint. */}
        {showToolbar && (
          <CanvasFloatingToolbarPortal
            positionAbsoluteX={positionAbsoluteX}
            positionAbsoluteY={positionAbsoluteY}
            width={errW}
            topOffset={64}
            onPointerDown={() => setIsToolbarHovered(true)}
          >
            <div
              onMouseEnter={() => setIsToolbarHovered(true)}
              onMouseLeave={() => setIsToolbarHovered(false)}
            >
              <BaseEntityCardFloatingToolbar
                visible={true}
                entityActions={[]}
                utilityActions={
                  data.error.sourceUrl
                    ? (() => {
                        const definition =
                          getObjectActionDefinition("open-in-new-tab");
                        const labelCtx = {
                          kind: {
                            type: "canvas-node" as const,
                            nodeType: "entity" as const,
                          },
                        };
                        return [
                          {
                            key: "open-in-new-tab",
                            label: getActionLabel(definition, labelCtx),
                            icon: createElement(
                              getActionIcon(definition, labelCtx),
                              {
                                className: "h-[17px] w-[17px]",
                              },
                            ),
                            onClick: () =>
                              openCanvasEntityTabUrl(data.error!.sourceUrl!),
                          },
                        ];
                      })()
                    : []
                }
                onChatClick={canEdit ? () => {} : undefined}
                onMoreClick={canEdit ? handleMoreClick : undefined}
              />
            </div>
          </CanvasFloatingToolbarPortal>
        )}

        <div
          className={cn(
            "w-full h-full rounded-xl overflow-hidden flex flex-col bg-neutral-900 border border-neutral-700/50 shadow-md",
            selected && "ring-2 ring-canvas-selection/60",
          )}
          onMouseEnter={() => setIsToolbarHovered(true)}
          onMouseLeave={() => setIsToolbarHovered(false)}
        >
          <div className="flex-1 p-4 flex flex-col gap-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-lc-warning" />
              <span className="text-sm font-semibold leading-tight text-lc-warning">
                {data.error.title}
              </span>
            </div>
            {data.error.message && (
              <p className="text-xs leading-relaxed text-neutral-400">
                {data.error.message}
              </p>
            )}
          </div>

          {data.error.sourceUrl && (
            <button
              className="flex items-center gap-2 border-t border-neutral-700/50 px-4 py-3 text-left text-neutral-500 transition-colors hover:text-neutral-300"
              onClick={() =>
                window.open(
                  data.error!.sourceUrl,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            >
              <ExternalLink className="h-3 w-3 shrink-0" />
              <span className="truncate text-xs">{data.error.sourceUrl}</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  if (showSkeleton) {
    const skeletonW = width || DEFAULT_WIDTH;
    const skeletonH = height || undefined;
    const entityType =
      (data.entity as { type?: string } | undefined)?.type ?? "";
    const contentType = fileRecordData?.content_type ?? "";
    const skeletonKind: FileRecordSkeletonKind =
      contentType.startsWith("image/") || entityType === "googleai_image"
        ? "image"
        : contentType.startsWith("video/") || entityType === "googleai_video"
          ? "video"
          : "file_record";
    const skeletonLabel =
      (layoutEntity?.data as { title?: string } | undefined)?.title ||
      undefined;
    return (
      <div className="relative" style={{ width: skeletonW, height: skeletonH }}>
        {/* No connect handles on the loading skeleton — they appear with the
            real card once media lands (see the updateNodeInternals nudge above). */}
        <FileRecordCardSkeleton
          width={skeletonW}
          height={skeletonH}
          kind={skeletonKind}
          label={skeletonLabel}
          // Same select/hover affordances as the real card so it's obvious the
          // skeleton is a normal node — selectable and deletable (useful for
          // manually cleaning up a placeholder whose tool run was lost).
          className={cn(
            "transition-shadow",
            !selected && "hover:ring-1 hover:ring-gold/30",
            selected && "ring-2 ring-canvas-selection/60",
          )}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative w-[350px]",
        isSectionPreviewMember && "ring-2 ring-canvas-selection/60 rounded-lg",
      )}
      style={{ width: nodeWidth !== DEFAULT_WIDTH ? nodeWidth : undefined }}
    >
      {/* React Flow edge connection handles — one grabbable source per side
          (loose connection mode lets each act as source or target). */}
      <CanvasConnectHandles />

      {/* NodeResizer — only shown when the node is selected. Outer condition
          gates the render (same pattern as ShapeNode) so the handles never
          exist in the DOM for unselected nodes. */}
      {isResizable && canEdit && !!selected && !dragging && (
        <NodeResizer
          keepAspectRatio={keepResizeAspect}
          minWidth={resizerMin}
          minHeight={resizerMin}
          onResizeStart={() => {
            setIsResizing(true);
            isResizingRef.current = true;
          }}
          onResizeEnd={() => {
            setIsResizing(false);
            isResizingRef.current = false;
          }}
        />
      )}

      {/* Floating toolbar — uses CanvasFloatingToolbarPortal so useViewport()
          is only subscribed when the toolbar is actually visible. Unselected
          nodes pay zero viewport-tracking cost. */}
      {showToolbar && (
        <CanvasFloatingToolbarPortal
          positionAbsoluteX={positionAbsoluteX}
          positionAbsoluteY={positionAbsoluteY}
          width={nodeWidth}
          topOffset={64}
          onPointerDown={() => setIsToolbarHovered(true)}
        >
          <div
            onMouseEnter={() => setIsToolbarHovered(true)}
            onMouseLeave={() => setIsToolbarHovered(false)}
          >
            {showChatInput ? (
              <BaseEntityCardQuickChat
                visible={true}
                onSubmit={handleChatSubmit}
                onClose={() => setShowChatInput(false)}
              />
            ) : (
              <BaseEntityCardFloatingToolbar
                visible={true}
                entityActions={entityActions}
                utilityActions={utilityActions}
                onChatClick={canEdit ? () => setShowChatInput(true) : undefined}
                onMoreClick={canEdit ? handleMoreClick : undefined}
              />
            )}
          </div>
        </CanvasFloatingToolbarPortal>
      )}

      {/* Entity card */}
      {data.entity ? (
        <div
          className="relative"
          data-entity-type={
            "type" in data.entity ? String(data.entity.type) : undefined
          }
          data-entity-base={
            "base" in data.entity ? String(data.entity.base) : undefined
          }
        >
          <CanvasEntityCard
            entity={
              (fileRecordCardEntity ?? layoutEntity ?? data.entity) as Entity
            }
            onDoubleClick={handleEntityClick}
            toolbarMode="external"
            selected={selected}
            resizing={isResizing}
            className={cn(
              // w-[350px] is the base class; inline style overrides it when resized.
              "rounded-lg relative w-[350px] border",
              // Disable hover shadow transition during resize to prevent flashing.
              !isResizing && "transition-shadow",
              !selected && "hover:border-gold/30",
              selected && "ring-2 ring-canvas-selection/60",
              isHighlighted &&
                "ring-2 ring-canvas-selection/80 dark:ring-canvas-selection/80",
              data?.entity && "cursor-pointer select-none",
            )}
            style={{
              width: nodeWidth !== DEFAULT_WIDTH ? nodeWidth : undefined,
              // Square cards (image/video/file) size themselves via aspect-square;
              // HTML file records get an explicit height to break the lock.
              ...(nodeHeight !== undefined && { height: nodeHeight }),
            }}
            onVideoDisplayReady={
              isVideoLocalPreview ? handleVideoDisplayReady : undefined
            }
          />
          {processes.length > 0 && (
            // Absolutely positioned so the badges float below the card without
            // expanding the node's measured bounding box / resize handles.
            <div className="absolute top-full left-0 right-0 flex flex-wrap justify-end gap-1 mt-1.5 px-1">
              <AnimatePresence mode="popLayout">
                {processes.map((process) => (
                  <LiveProcess
                    key={process.id}
                    process={process}
                    onNavigate={handleProcessNavigate}
                    hideWhenComplete
                  />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-card border-2 rounded-lg p-4 w-full flex items-center justify-center h-24">
          <div className="text-sm text-muted-foreground">Empty Entity Node</div>
        </div>
      )}
    </div>
  );
}
